/**
 * resolveTrainingEntry - the single decision point for where the dashboard's
 * training step sends a student (F1b sub-chunk 4).
 *
 * The credential training hijack is ACT-1 ONLY. During Act 1 a credential
 * cohort's lessons step routes into the Act-1 credential journey surface
 * (/dashboard/training/:capsuleId). Once Act 2 opens, the concepts/lessons step
 * is a normal Act-2 step again (Keyshawn lessons + concept collection) and MUST
 * revert to the standard ModePlayer - the hijack is only coherent while Act 1 is
 * live (Amir hands-on correction 2026-07-19). EVERY non-credential cohort
 * resolves to `default`, which the caller maps to the byte-identical prior
 * behavior. Pure function so the parity guard can prove non-credential routing
 * is unchanged, with no ModePlayer edits anywhere in F1.
 *
 * The `credential_bearing` flag rides on ChallengeResource (F1a A3); it is
 * coerced strictly to a boolean so an absent/degraded field can only ever
 * fall through to `default` (fail-safe toward the unchanged path).
 *
 * Act-awareness signal: `actState` is the clearance payload's `act_state`
 * (`{ mission_opened, ... }`) from the credentialClearance slice, passed by the
 * caller ONLY when it is loaded for the CURRENT capsule (a stale other-level
 * act_state must not divert routing). We revert to `default` ONLY when we KNOW
 * Act 2 is open (`mission_opened === true`). When the act state is UNKNOWN for a
 * credential cohort (clearance not yet loaded on the dashboard), routing to the
 * training journey is the correct default - Act 2 surfaces are independently
 * gated on the backend, so an early credential route can never expose Act 2.
 */
export function isCredentialBearing(challenge) {
  return challenge?.credential_bearing === true;
}

/**
 * usesV4Chain - does this cohort run the v4 journey chain (the 8-element
 * stepStatus: kickoff / mission / concepts / bridge / plan / work / huddle /
 * ship)?
 *
 * True when the challenge carries the explicit `usesV4Experience` flag, OR when
 * it is credential-bearing. A credential cohort is ALWAYS a v4 experience: its
 * effective-status ladder (deriveCredentialEffectiveStatus) and its Act-2 node
 * map (ACT2_STEP_INDEX) both address the v4 8-element stepStatus by index. If a
 * credential challenge's `usesV4Experience` flag is ever absent/false, the
 * dashboard would otherwise build the LEGACY stepStatus, whose index 0 opens the
 * ChallengeLaunchModal instead of navigating to the kickoff cinematic. On the
 * credential branch that modal never renders, so the credential rail's active
 * kickoff node would click into a no-op ("does nothing"). Deriving v4 from
 * credential_bearing keeps the credential branch on the IDENTICAL stock v4
 * launch mechanism (stepStatus[0] -> navigate to /challenges/:id/kickoff) with
 * no forked semantics.
 *
 * The flag half preserves the dashboard's existing truthy coercion (`!!`) EXACTLY
 * so no non-credential cohort's v4 resolution changes; the credential half is the
 * only added path. `isCredentialBearing` strictly coerces, so a non-credential /
 * degraded challenge can only ever fall through to the flag's own value.
 *
 * @param {object|null} challenge - the active challenge (ChallengeResource)
 * @returns {boolean}
 */
export function usesV4Chain(challenge) {
  return !!challenge?.usesV4Experience || isCredentialBearing(challenge);
}

/** True only when the act state is known AND Act 2 (the mission) is open. */
export function isAct2Open(actState) {
  return actState != null && actState.mission_opened === true;
}

/**
 * @param {object} args
 * @param {object|null} args.challenge - the active challenge (ChallengeResource)
 * @param {number|string|null} args.capsuleId - the current level's capsule id
 * @param {object|null} [args.actState] - clearance act_state for THIS capsule,
 *   or null/undefined when unknown (unknown → training, Act 2 gated regardless)
 * @returns {{mode:'credential', path:string} | {mode:'default'}}
 */
export function resolveTrainingEntry({ challenge, capsuleId, actState } = {}) {
  if (!isCredentialBearing(challenge)) return { mode: "default" };
  // Act 2 open → the lessons/concepts step reverts to the normal lesson player.
  if (isAct2Open(actState)) return { mode: "default" };
  const id = Number(capsuleId);
  if (Number.isInteger(id) && id > 0) {
    return { mode: "credential", path: `/dashboard/training/${id}` };
  }
  return { mode: "default" };
}

export default resolveTrainingEntry;
